import React, { Fragment, useState } from 'react';
import moment from 'moment';
import {
    Dialog,
    DialogPanel,
    Transition,
    TransitionChild,
} from '@headlessui/react';

import { setPreventUnload } from 'flow/actions/app-actions';
import Icon from 'flow/components/ui-components/Icon';
import { useModal } from 'flow/contexts/ModalProvider';
import useAppContext from 'flow/hooks/useAppContext';
import ImagePreviewBar from 'flow/pages/messages/ImagePreviewBar';
import ImagePreviewBottomBar from 'flow/pages/messages/ImagePreviewBottomBar';

interface Props {
    fileName?: string;
    url?: string;
    sentTime?: string;
    fileSizeBytes?: number;
}

const ImageTicketsModal = ({
    fileName,
    url,
    sentTime,
    fileSizeBytes,
}: Props) => {
    const { closeModal } = useModal();
    const [active, setActive] = useState(true);

    return (
        <Transition show={true} as={Fragment}>
            <Dialog
                static={true}
                as="div"
                className="relative z-20"
                onClose={closeModal}
            >
                <TransitionChild
                    as={Fragment}
                    enter="ease-out duration-300"
                    enterFrom="opacity-0"
                    enterTo="opacity-100"
                    leave="ease-in duration-200"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                >
                    <DialogPanel
                        className="no-dark fixed inset-0 h-screen w-screen"
                        onMouseEnter={() => setActive(true)}
                        onMouseLeave={() => setActive(false)}
                    >
                        <div className="flex h-full w-full flex-col gap-y-6 bg-black/70 backdrop-blur-sm">
                            <TopBar
                                url={url}
                                sentTime={sentTime}
                                visible={active}
                                fileSizeBytes={fileSizeBytes}
                            />
                            <ImageView fileName={fileName} url={url} />
                            <ImagePreviewBottomBar
                                fileName={fileName}
                                visible={active}
                            />
                        </div>
                    </DialogPanel>
                </TransitionChild>
            </Dialog>
        </Transition>
    );
};

export default ImageTicketsModal;

interface TopBarProps {
    url: string;
    sentTime: string;
    visible: boolean;
    fileSizeBytes?: number;
}

const TopBar = ({ url, sentTime, visible, fileSizeBytes }: TopBarProps) => {
    const { i18next } = useAppContext();
    const { closeModal } = useModal();
    const fileSizeUiFriendly = getFileSizeUiFriendly(fileSizeBytes);

    return (
        <ImagePreviewBar visible={visible}>
            <div className="flex h-13 w-full items-center justify-between gap-x-2 px-6 text-sm text-white">
                <div className="flex-1">
                    <time dateTime={sentTime}>
                        {moment(sentTime).format('HH:mm')}
                    </time>
                </div>

                {fileSizeUiFriendly ? (
                    <p className="text-gray-200">{`${fileSizeUiFriendly}`}</p>
                ) : null}

                <a
                    className="focus-ring"
                    href={url}
                    download={true}
                    onClick={() => setPreventUnload(true)}
                >
                    <Icon
                        iconKey="download"
                        className="p-0.5 text-white hover:bg-black dark:hover:text-gray-300"
                    />
                    <span className="sr-only">
                        {i18next.t('Tooltip.Audio.Download')}
                    </span>
                </a>

                <button
                    type="button"
                    className="focus-ring"
                    onClick={closeModal}
                >
                    <Icon
                        iconKey="close"
                        className="p-0.5 text-white hover:bg-black dark:hover:text-gray-300"
                    />
                    <span className="sr-only">
                        {i18next.t('Tooltip.Dialog.Close')}
                    </span>
                </button>
            </div>
        </ImagePreviewBar>
    );
};

interface ImageViewProps {
    url: string;
    fileName: string;
}

const ImageView = ({ url, fileName }: ImageViewProps) => {
    const { closeModal } = useModal();

    return (
        <div
            className="flex h-[calc(100%-38rem)] w-full grow items-center justify-center"
            onClick={(e) => {
                if (e.target === e.currentTarget) {
                    closeModal();
                }
            }}
        >
            <img
                src={url}
                className="max-h-full max-w-full object-contain"
                alt={fileName}
            />
        </div>
    );
};

const getFileSizeInKb = (fileSizeBytes: number) => fileSizeBytes / 1024;
const getFileSizeInMb = (fileSizeBytes: number) =>
    getFileSizeInKb(fileSizeBytes) / 1000;
const getFileSizeUiFriendly = (fileSizeBytes: number) => {
    if (!fileSizeBytes) return undefined;
    const fileSizeInMb = getFileSizeInMb(fileSizeBytes);
    if (fileSizeInMb < 1) {
        return `${Math.round(getFileSizeInKb(fileSizeBytes))} KB`;
    }
    return `${fileSizeInMb?.toFixed(2)} MB`;
};
